import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import { setAffectedVehiclesValidations, setVolumesAndOriginChanged } from '@/redux/Campaign/campaign.action';
import {
  getCampaignInfo, getCampaignValidations, getLimitationPlants, getOriginalValues,
} from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { AffectedVehiclesOriginalValues, CampaignInfo, LimitationPlant, Plant, Volume } from '@/types';
import RuleAccordion from './RuleAccordion/RuleAccordion';
import { validateVehicleVolume } from '../VehicleCleanDirt/VehicleCleanDirt-utils';
import { getOriginValuesNewState } from '../volumeDetails-utils';
import styles from '../VolumeDetails.module.scss';

interface CodeRuleProps {
  plantSelected: Plant;
  volumes: Volume[];
  setVolumesOriginhadChanged: any;// TODO:
  originalValues: AffectedVehiclesOriginalValues;
  campaignInfo: CampaignInfo;
  headerLanguage: string;
  defaultLanguage: string;
  validations: any;// TODO:
  setPlantsValidations: any;// TODO:
  limitationPlants: LimitationPlant[];
}

interface DialogConfirmProps {
  open: boolean;
  codeRuleToDelete?: number;
}

const CodeRule: React.FC<CodeRuleProps> = (props) => {
  const { plantSelected, volumes, setVolumesOriginhadChanged, originalValues, campaignInfo, headerLanguage, defaultLanguage,
    validations, setPlantsValidations, limitationPlants } = props;
  const { t } = useTranslation();
  const [dialogConfirmProps, setDialogConfirmProps] = useState<DialogConfirmProps>({ open: false });
  const volumeSelected = volumes?.find((currentVolume) => currentVolume?.plant === plantSelected?.wkzNumber);
  const codeRules = volumeSelected?.limitationVolumeManualEntry?.codeRules ?? [];
  const { isButtonDisabled, isReadOnly } = useCRPermissionMatrix(campaignInfo.status, campaignInfo.authorization);
  const checkDisabled = isReadOnly() || isButtonDisabled(CR_PERMISSION_ACTIONS.SAVE) || (headerLanguage !== defaultLanguage);
  const { detectLanguageTranslation } = useDetectLanguage();

  const newRules = (action: string, index?: number, label?: string, value?: string) => {
    if (action === 'add') {
      return [...codeRules, {
        codeRulesSelectionAccording: 'AFFECTED',
        codeRulesDescription: '',
        codeRuleComment: { uuid: uuidv4(), translations: { EN: '', DE: '' } },
      }];
    }

    if (action === 'delete') {
      return codeRules.filter((r, i) => i !== index);
    }

    return codeRules.map((rule, i) => (i === index
      ? {
        ...rule,
        [label || '']: label === 'codeRuleComment'
          ? { ...rule[label], translations: { ...rule[label].translations, [headerLanguage]: value } }
          : value,
      }
      : rule));
  };

  const onDelete = (index: number) => setDialogConfirmProps({ open: true, codeRuleToDelete: index });

  const onUpdateRules = (action: string, index?: number, label?: string, value?: string) => {
    const newState = volumes.map((v) => (v.plant === volumeSelected?.plant
      ? {
        ...v,
        limitationVolumeManualEntry: {
          ...v.limitationVolumeManualEntry,
          codeRules: newRules(action, index, label, value),
        },
      }
      : v));

    setVolumesOriginhadChanged({
      originalValues: getOriginValuesNewState(originalValues, plantSelected),
      volumes: newState,
    });
    validateVehicleVolume(newState, headerLanguage, validations, setPlantsValidations, plantSelected, limitationPlants);
    setDialogConfirmProps({ open: false });

    if (originalValues?.hasChange) {
      detectLanguageTranslation(label || '', plantSelected.wkzNumber);
    }
  };

  return (
    <>
      <div className={styles.TabContainer}>
        <div className={styles.CodeRuleHeader}>
          <div className={styles.HeaderTitle}>
            <strong>{t('affectedVehicles.CODE_RULE')}</strong>
          </div>
          <div>
            <SimpleButton
              disabled={checkDisabled}
              id="add-rule-button"
              size="small"
              color="primary"
              buttonText={t('codeRule.addRule')}
              handleClick={() => onUpdateRules('add')}
            />
          </div>
        </div>
        {codeRules?.map((rule, index) => (
          <RuleAccordion
            plantSelected={plantSelected}
            id={index}
            key={index}
            rule={rule}
            onChange={onUpdateRules}
            onDelete={onDelete}
          />
        ))}
      </div>

      {dialogConfirmProps.open && (
        <ConfirmDialog
          close={() => setDialogConfirmProps({ open: false })}
          confirm={() => onUpdateRules('delete', dialogConfirmProps.codeRuleToDelete)}
          title={t('dialog.deleteCodeRule.title')}
          message={t('dialog.deleteCodeRule.message')}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.confirm')}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  originalValues: getOriginalValues(state),
  validations: getCampaignValidations(state),
  campaignInfo: getCampaignInfo(state),
  defaultLanguage: getCampaignInfo(state)?.defaultLanguage,
  limitationPlants: getLimitationPlants(state),
});

const actionCreators = {
  setVolumesOriginhadChanged: setVolumesAndOriginChanged,
  setPlantsValidations: setAffectedVehiclesValidations,
};

export default connect(mapStateToProps, actionCreators)(CodeRule);
